import React, { useRef, useState } from 'react';
import "./FeedBack.css";
import { initializeStrapiBackend } from '../Contentful_API/strapiBackend';
import Swal from 'sweetalert2';
import Typography from '@material-ui/core/Typography';
import { FormControlLabel, Checkbox } from '@material-ui/core';

const highScoreOptions = [
  "Website loaded fast",
  "Content was clear",
  "Easy to find Content",
  "Easy to Navigate"
];

const lowScoreOptions = [
  "Couldn't find content",
  "Difficult to navigate",
  "Technical errors",
  "Content not clear",
  "Website loaded slowly",
  "Other"
];

const FeedBack = () => {   
  const submitting = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modal, SetModal] = useState(false);
  const [rating, setRating] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [details, setDetails] = useState('');
  const [email, setEmail] = useState('');

  const handleRatingClick = (value) => {
    // If switching between high and low categories, clear selected options
    if (rating) {
      const wasHigh = rating >= 4;
      const isHigh = value >= 4;
      if (wasHigh !== isHigh) {
        setSelectedOptions([]);
      }
    }
    setRating(value);
  };

  const handleOptionToggle = (option) => {
    setSelectedOptions(prev => 
      prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]
    );
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (submitting.current) return;
    if (!Number.isInteger(rating) || rating < 1 || rating > 6) {
      Swal.fire({ icon: 'error', title: 'Please select a rating from 1 to 6.' });
      return;
    }
    submitting.current = true;
    setIsSubmitting(true);
    try {
      const { apiUrl } = await initializeStrapiBackend();
      // Preserve negative reasons: the API only has positive checkbox fields.
      const submittedDetails = rating < 4 && selectedOptions.length
        ? `Areas: ${selectedOptions.join(', ')}\n${details}`
        : details;
      const response = await fetch(`${apiUrl}/website-feedbacks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({
          data: {
            rating: Number(rating),
            websiteLoadedFast: selectedOptions.includes('Website loaded fast'),
            contentWasClear: selectedOptions.includes('Content was clear'),
            easyToFindContent: selectedOptions.includes('Easy to find Content'),
            easyToNavigate: selectedOptions.includes('Easy to Navigate'),
            details: submittedDetails || '',
            email: email || '',
            pagePath: window.location.pathname,
          },
        }),
      });
      if (response.status !== 201) {
        throw new Error(response.status === 429
          ? 'Too many attempts. Please wait a minute and try again.'
          : 'Feedback could not be submitted. Please try again.');
      }
      Swal.fire({ icon: 'success', title: 'Thank you for your feedback!' });
      setRating(null);
      setSelectedOptions([]);
      setDetails('');
      setEmail('');
      SetModal(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Feedback could not be submitted',
        text: error instanceof TypeError
          ? 'Please check your connection and try again.'
          : error.message,
      });
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  };
  
  const currentOptions = rating >= 4 ? highScoreOptions : lowScoreOptions;

  return (
    <div>
      {!modal && (
        <button className="bt-feedback" onClick={() => SetModal(true)}>
          Help us Improve
        </button>
      )}
      <div>
        {modal && (
          <form className="feedbackForm right" onSubmit={submitFeedback} aria-busy={isSubmitting} style={{ overflowY: 'auto' }}>
            <h1 className="titleform">
              Help Us Improve
              <button type="button" className="btn-close-form" disabled={isSubmitting} onClick={() => SetModal(false)}>X</button>
            </h1>
            
            <div className='half' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
              <Typography component="legend" style={{ color: 'white', marginBottom: '10px' }}>
                How satisfied are you with our website?
              </Typography>
              <div className="rating-buttons-container">
                <span className="rating-label">NOT SATISFIED</span>
                {[1, 2, 3, 4, 5, 6].map(num => (
                  <button
                    type="button"
                    key={num}
                    disabled={isSubmitting}
                    aria-pressed={rating === num}
                    className={`rating-button ${rating === num ? 'selected' : ''}`}
                    onClick={() => handleRatingClick(num)}
                  >
                    {num}
                  </button>
                ))}
                <span className="rating-label">VERY SATISFIED</span>
              </div>
            </div>

            {rating && (
              <div className="feedback-step-two" style={{ textAlign: 'left', padding: '0 20px', color: 'white' }}>
                <Typography variant="subtitle1" style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                  Which areas contributed to your rating today?
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '15px' }}>
                  {currentOptions.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox 
                          disabled={isSubmitting}
                          checked={selectedOptions.includes(option)} 
                          onChange={() => handleOptionToggle(option)} 
                          style={{ color: 'white' }}
                        />
                      }
                      label={option}
                    />
                  ))}
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <textarea 
                    name="message_details" 
                    disabled={isSubmitting}
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder="Please provide more details about your experience. (Optional)" 
                    style={{ width: '100%', height: '80px' }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <input 
                    type="email" 
                    name="user_email" 
                    disabled={isSubmitting}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Please enter your email, should we need further clarifications we may contact you. (Optional)" 
                    style={{ width: '100%', fontSize: '13px' }}
                  />
                </div>

                <input type='submit' disabled={isSubmitting} value={isSubmitting ? 'Submitting...' : 'Submit Feedback'} id='input-submit' style={{ width: '100%', marginLeft: '0', marginBottom: '30px' }} />
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

export default FeedBack;
